import { gql } from "@/services/httpAdapter";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { first, get, values } from "lodash";

const saleCallBack = (
	queryClient: QueryClient,
	result: { data: { _id: string; commissionCycleId: string; locationId: string } },
	spiffId: string
) => {
	const locationId = get(first(values(result.data)), "locationId");
	const queryKey = ["spiffSetupEntry", spiffId, locationId];
	queryClient.invalidateQueries({
		queryKey,
	});
};
const serviceCallBack = (
	queryClient: QueryClient,
	result: { data: { _id: string; commissionCycleId: string; locationId: string } },
	spiffId: string
) => {
	const locationId = get(first(values(result.data)), "locationId");
	queryClient.invalidateQueries({
		queryKey: ["spiffBonusSetupEntry", spiffId, locationId],
	});
};

const useSpiffAwardsSubsForSetup = ({
	commissionCycleId,
	locationId,
	spiffId,
}: {
	commissionCycleId: string;
	locationId: string;
	spiffId: string;
}) => {
	const queryClient = useQueryClient();
	const { addSubscription, getSubscription } = useSubscriptions();

	const createSaleAwardSub = getSubscription([`onCreateSaleSpiffAward`, locationId, commissionCycleId]);
	const deleteSaleAwardSub = getSubscription([`onDeleteSaleSpiffAward`, locationId, commissionCycleId]);
	const createServiceAwardSub = getSubscription([`onCreateServiceSpiffAward`, locationId, commissionCycleId]);
	const deleteServiceAwardSub = getSubscription([`onDeleteServiceSpiffAward`, locationId, commissionCycleId]);

	createSaleAwardSub?.subscriptions?.[`onCreateSaleSpiffAward`]?.setCallback(
		(result: { data: { _id: string; commissionCycleId: string; locationId: string } }) => saleCallBack(queryClient, result, spiffId)
	);
	createServiceAwardSub?.subscriptions?.[`onCreateServiceSpiffAward`]?.setCallback(
		(result: { data: { _id: string; commissionCycleId: string; locationId: string } }) => serviceCallBack(queryClient, result, spiffId)
	);
	deleteSaleAwardSub?.subscriptions?.[`onDeleteSaleSpiffAward`]?.setCallback(
		(result: { data: { _id: string; commissionCycleId: string; locationId: string } }) => saleCallBack(queryClient, result, spiffId)
	);
	deleteServiceAwardSub?.subscriptions?.[`onDeleteServiceSpiffAward`]?.setCallback(
		(result: { data: { _id: string; commissionCycleId: string; locationId: string } }) => serviceCallBack(queryClient, result, spiffId)
	);

	useEffect(() => {
		if (!locationId || !commissionCycleId) {
			return;
		}

		addSubscription(
			[`onCreateSaleSpiffAward`, locationId, commissionCycleId],
			[
				{
					context: `onCreateSaleSpiffAward`,
					query: gql`
						subscription onCreateSaleSpiffAward($locationId: String, $commissionCycleId: String) {
							onCreateSaleSpiffAward(locationId: $locationId, commissionCycleId: $commissionCycleId) {
								_id
								locationId
								commissionCycleId
							}
						}
					`,
					variables: {
						locationId,
						commissionCycleId,
					},
				},
			]
		);

		addSubscription(
			[`onDeleteSaleSpiffAward`, locationId, commissionCycleId],
			[
				{
					context: `onDeleteSaleSpiffAward`,
					query: gql`
						subscription onDeleteSaleSpiffAward($locationId: String, $commissionCycleId: String) {
							onDeleteSaleSpiffAward(locationId: $locationId, commissionCycleId: $commissionCycleId) {
								_id
								locationId
								commissionCycleId
							}
						}
					`,
					variables: {
						locationId,
						commissionCycleId,
					},
				},
			]
		);
		addSubscription(
			[`onCreateServiceSpiffAward`, locationId, commissionCycleId],
			[
				{
					context: `onCreateServiceSpiffAward`,
					query: gql`
						subscription onCreateServiceSpiffAward($locationId: String, $commissionCycleId: String) {
							onCreateServiceSpiffAward(locationId: $locationId, commissionCycleId: $commissionCycleId) {
								_id
								locationId
								commissionCycleId
							}
						}
					`,
					variables: {
						locationId,
						commissionCycleId,
					},
				},
			]
		);

		addSubscription(
			[`onDeleteServiceSpiffAward`, locationId, commissionCycleId],
			[
				{
					context: `onDeleteServiceSpiffAward`,
					query: gql`
						subscription onDeleteServiceSpiffAward($locationId: String, $commissionCycleId: String) {
							onDeleteServiceSpiffAward(locationId: $locationId, commissionCycleId: $commissionCycleId) {
								_id
								locationId
								commissionCycleId
							}
						}
					`,
					variables: {
						locationId,
						commissionCycleId,
					},
				},
			]
		);
	}, [locationId, commissionCycleId]);
};

export default useSpiffAwardsSubsForSetup;
